import type { LucideIcon } from 'lucide-react';
import { normalizeForSearch } from '@/shared/lib/utils/normalizeForSearch';
import type { NavItem } from './buildNavItems';

export type NavCategoria = {
  codigo: number;
  rotulo: string;
  /** Ordem de exibicao no mega menu */
  ordem: number;
};

/** Alinhado a `categoria_modulo` em contracts/enums.yaml + categoria Geral.
 *  Ordem do mega menu (CSS columns): col1 Geral+Publicacao, col2 Cadastro,
 *  col3 Cardapio+Configuracoes. */
export const NAV_CATEGORIAS: NavCategoria[] = [
  { codigo: 0, rotulo: 'Geral', ordem: 0 },
  { codigo: 2, rotulo: 'Publicação', ordem: 1 },
  { codigo: 1, rotulo: 'Cadastro', ordem: 2 },
  { codigo: 3, rotulo: 'Cardápio', ordem: 3 },
  { codigo: 6, rotulo: 'Comunicação', ordem: 4 },
  { codigo: 5, rotulo: 'Configurações', ordem: 5 },
  { codigo: 4, rotulo: 'Evento', ordem: 6 },
];

const MODULO_CATEGORIA: Record<number, number> = {
  0: 0,
  10: 1,
  20: 1,
  30: 1,
  40: 1,
  50: 1,
  60: 1,
  70: 1,
  80: 2,
  90: 2,
  100: 2,
  110: 2,
  120: 2,
  130: 2,
  180: 2,
  140: 3,
  150: 3,
  160: 3,
  170: 4,
  190: 5,
  200: 5,
  230: 6,
  260: 0,
  270: 6,
  999: 5,
};

export function categoriaDoModulo(codigoModulo: number): number {
  return MODULO_CATEGORIA[codigoModulo] ?? 0;
}

export type NavGrupo = {
  categoria: NavCategoria;
  items: NavItem[];
};

export function agruparNavPorCategoria(items: NavItem[]): NavGrupo[] {
  const porCodigo = new Map<number, NavItem[]>();

  for (const item of items) {
    const cat = categoriaDoModulo(item.codigoModulo);
    const lista = porCodigo.get(cat) ?? [];
    lista.push(item);
    porCodigo.set(cat, lista);
  }

  return NAV_CATEGORIAS.filter((c) => porCodigo.has(c.codigo))
    .sort((a, b) => a.ordem - b.ordem)
    .map((categoria) => ({
      categoria,
      items: porCodigo.get(categoria.codigo) ?? [],
    }));
}

export function filtrarNavItems(items: NavItem[], consulta: string): NavItem[] {
  const q = normalizeForSearch(consulta);
  if (!q) return items;
  return items.filter((item) => normalizeForSearch(item.label).includes(q));
}

/** Entrada da busca detalhada do mega menu (modulo ou extra, ex.: um relatorio). */
export type MenuBuscaItem = {
  id: string;
  titulo: string;
  descricao: string;
  categoria: string;
  path: string;
  icon: LucideIcon;
  palavrasChave?: string[];
  /** Presente quando o item pode ser favoritado na sidebar. */
  codigoModulo?: number;
};

export function navItemParaBusca(item: NavItem): MenuBuscaItem {
  const codigo = categoriaDoModulo(item.codigoModulo);
  const categoria = NAV_CATEGORIAS.find((c) => c.codigo === codigo);
  return {
    id: `modulo-${item.codigoModulo}`,
    titulo: item.label,
    descricao: item.descricao,
    categoria: categoria?.rotulo ?? '',
    path: item.path,
    icon: item.icon,
    palavrasChave: item.palavrasChave,
    codigoModulo: item.codigoModulo,
  };
}

/** 0 = titulo comeca com a consulta, 1 = titulo contem, 2 = descricao, palavra-chave ou categoria. */
function rankBusca(item: MenuBuscaItem, q: string): number | null {
  const titulo = normalizeForSearch(item.titulo);
  if (titulo.startsWith(q)) return 0;
  if (titulo.includes(q)) return 1;
  const descricao = normalizeForSearch(item.descricao);
  const categoria = normalizeForSearch(item.categoria);
  const palavras = (item.palavrasChave ?? []).map((p) => normalizeForSearch(p));
  if (descricao.includes(q) || categoria.includes(q) || palavras.some((p) => p.includes(q))) {
    return 2;
  }
  return null;
}

/** Consulta vazia devolve a lista original. Caso contrario, filtra e ranqueia. */
export function buscarMenu(itens: MenuBuscaItem[], consulta: string): MenuBuscaItem[] {
  const q = normalizeForSearch(consulta);
  if (!q) return itens;
  return itens
    .map((item, index) => ({ item, index, rank: rankBusca(item, q) }))
    .filter(
      (entrada): entrada is { item: MenuBuscaItem; index: number; rank: number } => entrada.rank != null,
    )
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entrada) => entrada.item);
}
